// client/src/App.jsx

/**
 * This file serves as the core entry point for the frontend application. 
 * It coordinates connection parameters, defines experimental milestone steps (Consent, Quiz, Exit), 
 * and wraps the player interface inside the Empirica state context.
 */


import { EmpiricaClassic } from "@empirica/core/player/classic";
import { EmpiricaContext } from "@empirica/core/player/classic/react";
import { EmpiricaMenu, EmpiricaParticipant } from "@empirica/core/player/react";
import { Loading } from "@empirica/core/player/react";
import React from "react";
import { Game } from "./Game";
import { Consent } from "./intro-exit/Consent";
import { GameIntroAndQuiz } from "./intro-exit/GameIntroAndQuiz";
import { Exit } from "./intro-exit/Exit";
import { NoGame } from "./intro-exit/NoGame";

// ==========================================
// 1. Main Application Wrapper Component
// ==========================================
// ROBUST URL PARSER: Catches params whether they are before or after the "#".
// Module scope on purpose: the URL cannot change during a session, and the
// auto-register component below needs to read it without prop drilling.
export const getUrlParam = (key) => {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has(key)) return searchParams.get(key);

  const hash = window.location.hash;
  if (hash.includes("?")) {
    const hashParams = new URLSearchParams(hash.substring(hash.indexOf("?")));
    if (hashParams.has(key)) return hashParams.get(key);
  }
  return "";
};

// The participant identifier supplied by the recruitment platform.
// The recruitment link is expected to carry: ?workerId=...&sid=...&sesid=...
export const playerKey = getUrlParam("workerId") || "";

/**
 * Registers the participant automatically from the URL instead of showing the
 * "enter your ID" form.
 *
 * This is needed because passing `ns` to EmpiricaParticipant does NOT identify
 * anyone -- `ns` only namespaces the browser's localStorage keys
 * (`token:<ns>` / `participant:<ns>`), so on a first visit there is still no
 * session and Empirica falls back to its ID-entry form. onPlayerID() is the
 * call that actually registers, so we invoke it ourselves.
 *
 * Only used when the URL carries an ID; otherwise Empirica's default form is
 * left in place so the experiment can still be opened by hand for testing.
 */
function AutoRegisterPlayer({ onPlayerID }) {
  React.useEffect(() => {
    if (playerKey && onPlayerID) {
      onPlayerID(playerKey);
    }
  }, [onPlayerID]);

  return <Loading />;
}

/**
 * Shown instead of the experiment when the URL has no workerId.
 *
 * Participation is only valid through the recruitment link, so someone landing on
 * the bare domain must not be able to start a session: without a workerId there
 * would be no way to match their data back to a submission or pay them.
 */
function MissingRecruitmentLink() {
  return (
    <div className="min-h-screen bg-gray-50/50 flex items-center justify-center px-4 font-sans antialiased text-gray-900">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-900 px-6 py-4 text-white">
          <h1 className="app-page-title">Loan Risk Assessment Experiment</h1>
        </div>
        <div className="p-8 space-y-3 text-center">
          <h2 className="app-section-title text-gray-800">This study cannot be opened directly</h2>
          <p className="app-body text-gray-600">
            Please return to the study listing and use the participation link provided
            there. That link identifies your submission so your responses can be
            recorded and paid.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Replaces Empirica's built-in "no experiments available" screen.
 *
 * The stock version tells participants to "wait until an experiment becomes
 * available", which is wrong for a Prolific run -- nobody should sit on this page
 * hoping a batch opens -- and in a development build it also renders a "Go to
 * Admin to get started" link, which participants should never be shown.
 */
function NoExperiments() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 font-sans antialiased">
      <div className="max-w-sm w-full flex flex-col items-center text-center">
        <h2 className="app-section-title text-gray-700">No experiments available</h2>
        <p className="app-body mt-2 text-gray-500">
          There are currently no available experiments. Please come back at a later date.
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const { protocol, host } = window.location;
  const url = `${protocol}//${host}/query`;

  // Hard gate: never mount the Empirica participant context without a workerId,
  // so no session, player, or partial data can be created from a bare URL.
  if (!playerKey) {
    return <MissingRecruitmentLink />;
  }

  // ==========================================
  // 2. Experimental Step Flow Routes
  // ==========================================
  function introSteps({ game, player }) {
    return [Consent, GameIntroAndQuiz];
  }

  /**
   * Empirica renders the exit steps whenever `player.get("ended")` is set, but that
   * one attribute covers both "finished the study" and "never got to play".
   * Participants who never played have nothing to answer a survey about, so they
   * skip straight to the completion code and are still paid the base wage.
   *
   * Two things make this trickier than testing `ended` alone:
   *
   * 1. `player.exit(reason)` stores the *reason string*, so a normal finish leaves
   *    "completed final round" (Stage.jsx and callbacks.js), NOT "game ended".
   *    Anything written as a denylist of one value would divert every finisher.
   * 2. When a batch is terminated, Empirica does a plain `set("ended", ...)` over
   *    every player in its games, including ones who already finished. Someone
   *    part-way through the survey would otherwise be yanked onto the sorry page
   *    and lose their answers.
   *
   * So: an allowlist of the reasons that genuinely mean "no completed game", and a
   * guard on the server-set completion flag that wins over any later overwrite.
   * Unrecognised reasons fall through to the survey, which is the safe default.
   */
  function exitSteps({ game, player }) {
    const NO_GAME_REASONS = ["no more games", "game terminated", "game failed"];
    const completed = player?.get("exitStatus") === "finished";
    const ended = player?.get("ended");

    if (!completed && NO_GAME_REASONS.includes(ended)) {
      return [() => <NoGame player={player} game={game} />];
    }
    return [() => <Exit player={player} game={game} />];
  }

  // ==========================================
  // 3. Application State Context Rendering
  // ==========================================
  return (
    <EmpiricaParticipant url={url} ns={playerKey} modeFunc={EmpiricaClassic}>
      <div className="h-screen relative">
        {/* <EmpiricaMenu position="bottom-left" /> */}
        <div className="h-full overflow-auto">
          {/*
            disableConsent removes Empirica's built-in short consent screen, so the
            first thing a participant sees is our own Consent step in introSteps.

            playerCreate registers the workerId from the URL instead of prompting.
            Reaching this point guarantees a workerId exists (see the gate above).

            noGames replaces the stock "no experiments available" screen, which
            otherwise tells participants to wait and, in dev builds, links to Admin.
          */}
          <EmpiricaContext
            disableConsent
            noGames={NoExperiments}
            playerCreate={AutoRegisterPlayer}
            introSteps={introSteps}
            exitSteps={exitSteps}
          >
            <Game />
          </EmpiricaContext>
        </div>
      </div>
    </EmpiricaParticipant>
  );
}
